// Schema-diff engine. Compares two row sets keyed by `idField` and returns
// per-row + per-column differences for the Schema Diff Viewer.

/**
 * @param {string} idField  — primary-key column on each row (e.g. controlId)
 * @param {Array}  baseRows
 * @param {Array}  compareRows
 * @returns {{
 *   counts: { base:number, compare:number, added:number, removed:number, modified:number, unchanged:number },
 *   columns: { added:string[], removed:string[], common:string[], stable:string[] },
 *   added: Array,
 *   removed: Array,
 *   modified: Array<{ id:string, base:object, compare:object, changes: Array<{ field:string, before:any, after:any }> }>,
 *   unchanged: Array,
 * }}
 */
export function diffMasterRows(idField, baseRows = [], compareRows = []) {
  const ignoreFields = new Set(['_sourceFile', '_sourceSheet', '_sourceRow']);

  // Column sets
  const baseCols = new Set();
  const compCols = new Set();
  baseRows.forEach((r) => Object.keys(r || {}).forEach((k) => baseCols.add(k)));
  compareRows.forEach((r) => Object.keys(r || {}).forEach((k) => compCols.add(k)));

  const columns = {
    added: [...compCols].filter((c) => !baseCols.has(c)).sort(),
    removed: [...baseCols].filter((c) => !compCols.has(c)).sort(),
    common: [...compCols].filter((c) => baseCols.has(c)).sort(),
  };

  // Row diff keyed by idField
  const baseMap = new Map(baseRows.filter((r) => r && r[idField] != null).map((r) => [String(r[idField]), r]));
  const compMap = new Map(compareRows.filter((r) => r && r[idField] != null).map((r) => [String(r[idField]), r]));

  const added = [];
  const removed = [];
  const modified = [];
  const unchanged = [];
  const stableSet = new Set(columns.common);
  const fieldsToCompare = columns.common.filter((c) => c !== idField && !ignoreFields.has(c));

  compMap.forEach((compRow, id) => {
    const baseRow = baseMap.get(id);
    if (!baseRow) { added.push(compRow); return; }
    const changes = [];
    fieldsToCompare.forEach((f) => {
      const before = baseRow[f];
      const after = compRow[f];
      if (!stableEqual(before, after)) {
        changes.push({ field: f, before, after });
        stableSet.delete(f);
      }
    });
    if (changes.length) modified.push({ id, base: baseRow, compare: compRow, changes });
    else unchanged.push(compRow);
  });
  baseMap.forEach((baseRow, id) => {
    if (!compMap.has(id)) removed.push(baseRow);
  });

  return {
    counts: {
      base: baseRows.length,
      compare: compareRows.length,
      added: added.length,
      removed: removed.length,
      modified: modified.length,
      unchanged: unchanged.length,
    },
    columns: { ...columns, stable: [...stableSet].sort() },
    added,
    removed,
    modified,
    unchanged,
  };
}

function stableEqual(a, b) {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;
  if (typeof a === 'object') return JSON.stringify(a) === JSON.stringify(b);
  return String(a).trim() === String(b).trim();
}
